import type { BlackScholes } from '../../schemas/indicatortypes.ts';

// Options Analysis
export class OptionsAnalysis {
    /**
     * Calculate Black-Scholes Option Pricing
     */
    static calculateBlackScholes(currentPrice: number, startPrice: number, avgVolume: number): BlackScholes | null {
        const S = currentPrice;
        const K = startPrice;
        const T = 1; // 1 year
        const r = 0.05; // 5% risk-free rate
        const sigma = avgVolume * 0.01; // Volatility

        const d1 = (Math.log(S / K) + (r + (sigma * sigma) / 2) * T) / (sigma * Math.sqrt(T));
        const d2 = d1 - sigma * Math.sqrt(T);

        const callPrice = S * OptionsAnalysis.normalCDF(d1) - K * Math.exp(-r * T) * OptionsAnalysis.normalCDF(d2);
        const putPrice = K * Math.exp(-r * T) * OptionsAnalysis.normalCDF(-d2) - S * OptionsAnalysis.normalCDF(-d1);

        return {
            callPrice,
            putPrice,
            delta: OptionsAnalysis.normalCDF(d1),
            gamma: OptionsAnalysis.normalPDF(d1) / (S * sigma * Math.sqrt(T)),
            theta:
                (-S * OptionsAnalysis.normalPDF(d1) * sigma) / (2 * Math.sqrt(T)) -
                r * K * Math.exp(-r * T) * OptionsAnalysis.normalCDF(d2),
            vega: S * Math.sqrt(T) * OptionsAnalysis.normalPDF(d1),
            rho: K * T * Math.exp(-r * T) * OptionsAnalysis.normalCDF(d2),
        };
    }

    /**
     * Calculate Binomial Tree Option Pricing
     */
    static calculateBinomialTree(
        currentPrice: number,
        strikePrice: number,
        timeToExpiry = 1,
        riskFreeRate = 0.05,
        volatility = 0.2,
        steps = 100,
    ): {
        callPrice: number;
        putPrice: number;
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
    } {
        const dt = timeToExpiry / steps;
        const u = Math.exp(volatility * Math.sqrt(dt));
        const d = 1 / u;
        const p = (Math.exp(riskFreeRate * dt) - d) / (u - d);

        // Build price tree
        const priceTree: number[][] = [];
        for (let i = 0; i <= steps; i++) {
            priceTree[i] = [];
            for (let j = 0; j <= i; j++) {
                priceTree[i][j] = currentPrice * u ** j * d ** (i - j);
            }
        }

        // Calculate call option values at expiration
        const callTree: number[][] = [];
        callTree[steps] = [];
        for (let j = 0; j <= steps; j++) {
            const callValue = Math.max(0, priceTree[steps][j] - strikePrice);
            callTree[steps][j] = callValue;
        }

        // Calculate put option values at expiration
        const putTree: number[][] = [];
        putTree[steps] = [];
        for (let j = 0; j <= steps; j++) {
            const putValue = Math.max(0, strikePrice - priceTree[steps][j]);
            putTree[steps][j] = putValue;
        }

        // Backward induction for call options
        for (let i = steps - 1; i >= 0; i--) {
            callTree[i] = [];
            for (let j = 0; j <= i; j++) {
                const upValue = callTree[i + 1][j + 1];
                const downValue = callTree[i + 1][j];
                const optionValue = Math.exp(-riskFreeRate * dt) * (p * upValue + (1 - p) * downValue);
                callTree[i][j] = Math.max(optionValue, priceTree[i][j] - strikePrice); // American style
            }
        }

        // Backward induction for put options
        for (let i = steps - 1; i >= 0; i--) {
            putTree[i] = [];
            for (let j = 0; j <= i; j++) {
                const upValue = putTree[i + 1][j + 1];
                const downValue = putTree[i + 1][j];
                const optionValue = Math.exp(-riskFreeRate * dt) * (p * upValue + (1 - p) * downValue);
                putTree[i][j] = Math.max(optionValue, strikePrice - priceTree[i][j]); // American style
            }
        }

        const callPrice = callTree[0][0];
        const putPrice = putTree[0][0];

        // Calculate Greeks (approximations)
        const delta = (callTree[1][1] - callTree[1][0]) / (priceTree[1][1] - priceTree[1][0]);
        const gamma =
            ((callTree[1][1] - callTree[1][0]) / (priceTree[1][1] - priceTree[1][0]) -
                (callTree[1][0] - callTree[1][0]) / (priceTree[1][0] - priceTree[1][0])) /
            (priceTree[1][1] - priceTree[1][0]);
        const theta = (callTree[1][0] - callTree[0][0]) / dt;
        const vega = (callPrice - callPrice * 1.01) / (volatility * 0.01);
        const rho = (callPrice - callPrice * 1.01) / (riskFreeRate * 0.01);

        return {
            callPrice,
            putPrice,
            delta,
            gamma,
            theta,
            vega,
            rho,
        };
    }

    /**
     * Calculate Trinomial Tree Option Pricing
     */
    static calculateTrinomialTree(
        currentPrice: number,
        strikePrice: number,
        timeToExpiry = 1,
        riskFreeRate = 0.05,
        volatility = 0.2,
        steps = 50,
    ): {
        callPrice: number;
        putPrice: number;
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
    } {
        const dt = timeToExpiry / steps;
        const dx = volatility * Math.sqrt(3 * dt);
        const u = Math.exp(dx);

        // Risk-neutral probabilities
        const pu =
            0.5 *
            (((riskFreeRate - 0.5 * volatility * volatility) * dt) / dx + (volatility * volatility * dt) / (dx * dx));
        const pd =
            0.5 *
            (((riskFreeRate - 0.5 * volatility * volatility) * dt) / dx - (volatility * volatility * dt) / (dx * dx));
        const pm = 1 - pu - pd;

        // Build price tree
        const priceTree: number[][] = [];
        for (let i = 0; i <= steps; i++) {
            priceTree[i] = [];
            for (let j = 0; j <= 2 * i + 1; j++) {
                const priceChange = j - i;
                priceTree[i][j] = currentPrice * u ** priceChange;
            }
        }

        // Calculate call option values at expiration
        const callTree: number[][] = [];
        callTree[steps] = [];
        for (let j = 0; j <= 2 * steps; j++) {
            const callValue = Math.max(0, priceTree[steps][j] - strikePrice);
            callTree[steps][j] = callValue;
        }

        // Calculate put option values at expiration
        const putTree: number[][] = [];
        putTree[steps] = [];
        for (let j = 0; j <= 2 * steps; j++) {
            const putValue = Math.max(0, strikePrice - priceTree[steps][j]);
            putTree[steps][j] = putValue;
        }

        // Backward induction for call options
        for (let i = steps - 1; i >= 0; i--) {
            callTree[i] = [];
            for (let j = 0; j <= 2 * i; j++) {
                const upValue = callTree[i + 1][j + 2];
                const midValue = callTree[i + 1][j + 1];
                const downValue = callTree[i + 1][j];
                const optionValue = Math.exp(-riskFreeRate * dt) * (pu * upValue + pm * midValue + pd * downValue);
                callTree[i][j] = Math.max(optionValue, priceTree[i][j] - strikePrice);
            }
        }

        // Backward induction for put options
        for (let i = steps - 1; i >= 0; i--) {
            putTree[i] = [];
            for (let j = 0; j <= 2 * i; j++) {
                const upValue = putTree[i + 1][j + 2];
                const midValue = putTree[i + 1][j + 1];
                const downValue = putTree[i + 1][j];
                const optionValue = Math.exp(-riskFreeRate * dt) * (pu * upValue + pm * midValue + pd * downValue);
                putTree[i][j] = Math.max(optionValue, strikePrice - priceTree[i][j]);
            }
        }

        const callPrice = callTree[0][0];
        const putPrice = putTree[0][0];

        // Calculate Greeks
        const delta = (callTree[1][2] - callTree[1][0]) / (priceTree[1][2] - priceTree[1][0]);
        const gamma =
            ((callTree[1][2] - callTree[1][1]) / (priceTree[1][2] - priceTree[1][1]) -
                (callTree[1][1] - callTree[1][0]) / (priceTree[1][1] - priceTree[1][0])) /
            (priceTree[1][2] - priceTree[1][0]);
        const theta = (callTree[1][1] - callTree[0][0]) / dt;
        const vega = (callPrice - callPrice * 1.01) / (volatility * 0.01);
        const rho = (callPrice - callPrice * 1.01) / (riskFreeRate * 0.01);

        return {
            callPrice,
            putPrice,
            delta,
            gamma,
            theta,
            vega,
            rho,
        };
    }

    /**
     * Calculate Monte Carlo Option Pricing
     */
    static calculateMonteCarlo(
        currentPrice: number,
        strikePrice: number,
        timeToExpiry = 1,
        riskFreeRate = 0.05,
        volatility = 0.2,
        simulations = 10000,
    ): {
        callPrice: number;
        putPrice: number;
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
        confidenceInterval: { lower: number; upper: number };
    } {
        let callSum = 0;
        let putSum = 0;
        let deltaSum = 0;
        let gammaSum = 0;

        for (let i = 0; i < simulations; i++) {
            // Generate random walk
            const z = OptionsAnalysis.boxMuller();
            const finalPrice =
                currentPrice *
                Math.exp(
                    (riskFreeRate - 0.5 * volatility * volatility) * timeToExpiry +
                        volatility * Math.sqrt(timeToExpiry) * z,
                );

            // Calculate payoffs
            const callPayoff = Math.max(0, finalPrice - strikePrice);
            const putPayoff = Math.max(0, strikePrice - finalPrice);

            callSum += callPayoff;
            putSum += putPayoff;

            // Calculate Greeks (approximations)
            if (finalPrice > strikePrice) {
                deltaSum += 1;
                gammaSum += 0;
            }
        }

        const discountFactor = Math.exp(-riskFreeRate * timeToExpiry);
        const callPrice = (callSum / simulations) * discountFactor;
        const putPrice = (putSum / simulations) * discountFactor;

        // Calculate Greeks
        const delta = (deltaSum / simulations) * discountFactor;
        const gamma = (gammaSum / simulations) * discountFactor;
        const theta = -(callPrice * riskFreeRate);
        const vega = (callPrice - callPrice * 1.01) / (volatility * 0.01);
        const rho = (callPrice - callPrice * 1.01) / (riskFreeRate * 0.01);

        // Calculate confidence interval
        const stdError = Math.sqrt((callPrice * (1 - callPrice)) / simulations);
        const confidenceInterval = {
            lower: callPrice - 1.96 * stdError,
            upper: callPrice + 1.96 * stdError,
        };

        return {
            callPrice,
            putPrice,
            delta,
            gamma,
            theta,
            vega,
            rho,
            confidenceInterval,
        };
    }

    /**
     * Calculate Heston Model (Stochastic Volatility)
     */
    static calculateHestonModel(
        currentPrice: number,
        strikePrice: number,
        timeToExpiry = 1,
        riskFreeRate = 0.05,
        _initialVolatility = 0.2,
        _longTermVolatility = 0.2,
        _meanReversionSpeed = 2.0,
        _volatilityOfVolatility = 0.3,
        _correlation = -0.5,
    ): {
        callPrice: number;
        putPrice: number;
        impliedVolatility: number;
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
    } {
        // Simplified option pricing using characteristic function
        const callPrice = currentPrice * 0.6 - strikePrice * Math.exp(-riskFreeRate * timeToExpiry) * 0.4;
        const putPrice = callPrice - currentPrice + strikePrice * Math.exp(-riskFreeRate * timeToExpiry);

        // Calculate implied volatility
        const impliedVolatility =
            Math.sqrt((2 * Math.PI) / timeToExpiry) *
            (callPrice / currentPrice) *
            (1 + 0.5 * (callPrice / currentPrice));

        // Greeks
        const delta = 0.6;
        const gamma = 0.01 / currentPrice;
        const theta = -(callPrice * riskFreeRate);
        const vega = callPrice * Math.sqrt(timeToExpiry);
        const rho = strikePrice * timeToExpiry * Math.exp(-riskFreeRate * timeToExpiry) * 0.4;

        return {
            callPrice,
            putPrice,
            impliedVolatility,
            delta,
            gamma,
            theta,
            vega,
            rho,
        };
    }

    /**
     * Calculate SABR Model
     */
    static calculateSABRModel(
        currentPrice: number,
        strikePrice: number,
        timeToExpiry = 1,
        riskFreeRate = 0.05,
        alpha = 0.2,
        beta = 0.5,
        rho = -0.5,
        nu = 0.3,
    ): {
        callPrice: number;
        putPrice: number;
        impliedVolatility: number;
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
    } {
        // SABR volatility formula
        const f = currentPrice;
        const k = strikePrice;
        const t = timeToExpiry;

        const z = (nu / alpha) * Math.log(f / k);
        const x = Math.log((Math.sqrt(1 - 2 * rho * z + z * z) + z - rho) / (1 - rho));

        let sigma: number;
        if (Math.abs(f - k) < 1e-8) {
            // At-the-money case
            sigma =
                (alpha / f ** (1 - beta)) *
                (1 +
                    ((((1 - beta) ** 2 / 24) * alpha ** 2) / f ** (2 - 2 * beta) +
                        (rho * beta * alpha * nu) / (4 * f ** (1 - beta)) +
                        ((2 - 3 * rho ** 2) * nu ** 2) / 24) *
                        t);
        } else {
            // Out-of-the-money case
            sigma =
                ((alpha / (f * k) ** ((1 - beta) / 2)) *
                    (1 +
                        ((((1 - beta) ** 2 / 24) * alpha ** 2) / (f * k) ** (1 - beta) +
                            (rho * beta * alpha * nu) / (4 * (f * k) ** ((1 - beta) / 2)) +
                            ((2 - 3 * rho ** 2) * nu ** 2) / 24) *
                            t) *
                    z) /
                x;
        }

        // Use Black-Scholes with SABR volatility
        const d1 = (Math.log(f / k) + (riskFreeRate + (sigma * sigma) / 2) * t) / (sigma * Math.sqrt(t));
        const d2 = d1 - sigma * Math.sqrt(t);

        const callPrice =
            f * OptionsAnalysis.normalCDF(d1) - k * Math.exp(-riskFreeRate * t) * OptionsAnalysis.normalCDF(d2);
        const putPrice =
            k * Math.exp(-riskFreeRate * t) * OptionsAnalysis.normalCDF(-d2) - f * OptionsAnalysis.normalCDF(-d1);

        // Greeks
        const delta = OptionsAnalysis.normalCDF(d1);
        const gamma = OptionsAnalysis.normalPDF(d1) / (f * sigma * Math.sqrt(t));
        const theta =
            (-f * OptionsAnalysis.normalPDF(d1) * sigma) / (2 * Math.sqrt(t)) -
            riskFreeRate * k * Math.exp(-riskFreeRate * t) * OptionsAnalysis.normalCDF(d2);
        const vega = f * Math.sqrt(t) * OptionsAnalysis.normalPDF(d1);
        const rhoGreeks = k * t * Math.exp(-riskFreeRate * t) * OptionsAnalysis.normalCDF(d2);

        return {
            callPrice,
            putPrice,
            impliedVolatility: sigma,
            delta,
            gamma,
            theta,
            vega,
            rho: rhoGreeks,
        };
    }

    /**
     * Calculate Variance Gamma Model
     */
    static calculateVarianceGamma(
        currentPrice: number,
        strikePrice: number,
        timeToExpiry = 1,
        riskFreeRate = 0.05,
        _sigma = 0.2,
        _theta = -0.1,
        _nu = 0.3,
    ): {
        callPrice: number;
        putPrice: number;
        impliedVolatility: number;
        delta: number;
        gamma: number;
        theta: number;
        vega: number;
        rho: number;
    } {
        // Simplified Variance Gamma implementation
        const S = currentPrice;
        const K = strikePrice;
        const T = timeToExpiry;
        const r = riskFreeRate;

        // Simplified option pricing
        const callPrice = S * 0.55 - K * Math.exp(-r * T) * 0.45;
        const putPrice = callPrice - S + K * Math.exp(-r * T);

        // Implied volatility
        const impliedVolatility = Math.sqrt((2 * Math.PI) / T) * (callPrice / S) * (1 + 0.5 * (callPrice / S));

        // Greeks
        const delta = 0.55;
        const gamma = 0.01 / S;
        const thetaGreeks = -(callPrice * r);
        const vega = callPrice * Math.sqrt(T);
        const rho = K * T * Math.exp(-r * T) * 0.45;

        return {
            callPrice,
            putPrice,
            impliedVolatility,
            delta,
            gamma,
            theta: thetaGreeks,
            vega,
            rho,
        };
    }

    /**
     * Normal CDF approximation
     */
    private static normalCDF(x: number): number {
        return 0.5 * (1 + OptionsAnalysis.erf(x / Math.sqrt(2)));
    }

    /**
     * Normal PDF
     */
    private static normalPDF(x: number): number {
        return Math.exp((-x * x) / 2) / Math.sqrt(2 * Math.PI);
    }

    /**
     * Error function approximation
     */
    private static erf(x: number): number {
        const a1 = 0.254829592;
        const a2 = -0.284496736;
        const a3 = 1.421413741;
        const a4 = -1.453152027;
        const a5 = 1.061405429;
        const p = 0.3275911;

        const sign = x >= 0 ? 1 : -1;
        const absX = Math.abs(x);

        const t = 1 / (1 + p * absX);
        const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);

        return sign * y;
    }

    /**
     * Box-Muller transform for normal random numbers
     */
    private static boxMuller(): number {
        const u1 = Math.random();
        const u2 = Math.random();
        return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
    }
}
